import { HttpClient, HttpEventType, HttpParams, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap, takeUntil, tap, throwError } from 'rxjs';
import { ShellDomainProvider } from '../standalone-packages/ecap3/core';
import { FileInfo } from '../models/file-info.model';
import { FileService } from './file.service';
import { MimeMapperService } from './mime-mapper.service';
import { SCConfig } from '../../sc-config/constant/sc-config.constant';

@Injectable({
    providedIn: 'root'
})
export class IpexFileService {
    private readonly fileServiceBaseUrl: string = `${ShellDomainProvider['api'].IpexFileService}/`;

    constructor(
        private readonly httpClient: HttpClient,
        private readonly fileService: FileService,
        private readonly mimeMapper: MimeMapperService
    ) { }

    public loadInsuranceCompanyLogos(
        companyList: any[],
        mappingObject: { [companyId: string]: any },
        destroy$: Observable<boolean>
    ): void {
        if (!companyList || !mappingObject) return;

        companyList.forEach(company => {
            mappingObject[company.ItemId] = {
                CompanyId: company.ItemId,
                DisplayName: company.Name,
                FileId: company.LogoFileId,
                Src: null
            };
        });

        const fileIds = Array.from(
            new Set(companyList.filter(company => company.LogoFileId).map(company => company.LogoFileId))
        ) as string[];

        fileIds.forEach(fileId => {
            if (!fileId) return;
            this.getFileUrlById(fileId)
                .pipe(takeUntil(destroy$))
                .subscribe({
                    next: res => {
                        const matchingCompanies = companyList.filter(company => company.LogoFileId === fileId);
                        matchingCompanies.forEach(company => {
                            if (mappingObject[company.ItemId]) {
                                mappingObject[company.ItemId].Src = res;
                            }
                        });
                    },
                    error: err => {
                        console.error(`Failed to get file URL for logo file ID: ${fileId}`, err);
                    }
                });
        });
    }

    public uploadWithId(file: File, fileId: string, label?: string): Observable<any> {
        if (!file || !fileId) {
            return throwError(() => new Error('Invalid file or file ID provided'));
        }

        const formData = this.createFileUploadFormData(file, fileId, label);
        const uploadEndpoint = `${this.fileServiceBaseUrl}fileservice/v1/file/uploadWithId`;

        return this.httpClient
            .post(uploadEndpoint, formData, {
                reportProgress: true,
                observe: 'events'
            })
            .pipe(
                catchError(error => {
                    console.error('File upload failed:', error);
                    return throwError(() => error);
                })
            );
    }

    public downloadFileById(fileId: string, label?: string, imageSize?: string): Observable<Blob> {
        if (!fileId) {
            return throwError(() => new Error('Invalid file ID provided'));
        }

        let downloadEndpoint = `${this.fileServiceBaseUrl}fileservice/v1/file/getFile?id=${fileId}`;

        // imageSize = SCConfig.ImageSize.THUMBNAIL;

        if (imageSize) downloadEndpoint += `&imageSize=${imageSize || SCConfig.ImageSize.ORIGINAL}`;

        return this.httpClient
            .get(`${downloadEndpoint}`, {
                responseType: 'blob'
            })
            .pipe(
                catchError(error => {
                    if (error.status === 404) {
                        return this.handle404Fallback('File download', fileId, () =>
                            this.migrateFileFromSeliseToIpex(fileId, undefined, label)
                        );
                    }
                    return throwError(() => error);
                })
            );
    }

    public downloadCompressedFiles(archiveName: string, fileIds: string[]): Observable<HttpEvent<Blob>> {
        const downloadEndpoint = `${this.fileServiceBaseUrl}/fileservice/v1/file/getFilesCompressed`;
        const params = new HttpParams().set('archiveName', archiveName);

        return this.httpClient
            .post(downloadEndpoint, fileIds, {
                reportProgress: true,
                observe: 'events',
                responseType: 'blob',
                params
            })
            .pipe(
                catchError(error => {
                    console.error('Compressed files download failed:', error);
                    return throwError(() => error);
                })
            );
    }

    public handleFileUploadProgressEvent(event: any, uploadItem: FileInfo): FileInfo {
        if (event.type === HttpEventType.UploadProgress) {
            const totalBytes = event.total || 1; // Avoid division by zero
            uploadItem.Progress = Math.round(100 * (event.loaded / totalBytes));
        }
        return uploadItem;
    }

    public getFileUrlById(fileId: string, label?: string, imageSize?: string): Observable<string> {
        return this.downloadFileById(fileId, label, imageSize).pipe(
            map(blob => URL.createObjectURL(blob)),
            catchError(error => {
                // console.error(`Error downloading profile image for fileId: ${fileId}`, error);
                return throwError(() => error);
            })
        );
    }

    public downloadFileFromBlob(blob: Blob, fileName: string) {
        try {
            const blobUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.setAttribute('href', blobUrl);
            a.setAttribute('target', '_blank');
            a.setAttribute('download', fileName);
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        } catch (error) {
            console.error('error while downloading file', error);
        }
    }

    public getFileMetaData(fileId: string): Observable<any> {
        if (!fileId) {
            return throwError(() => new Error('Invalid file ID provided'));
        }

        const metadataEndpoint = `${this.fileServiceBaseUrl}fileservice/v1/file/getFileMetaData`;

        return this.httpClient.get(`${metadataEndpoint}?id=${fileId}`).pipe(
            catchError(error => {
                if (error.status === 404) {
                    return this.handle404Fallback('File metadata', fileId, () =>
                        this.fileService.getFileById(fileId).pipe(
                            map((fileResponse: any) => {
                                const metadata = {
                                    name: fileResponse.Name,
                                    size: fileResponse.Size,
                                    type: fileResponse.Type,
                                    url: fileResponse.Url
                                };

                                // Trigger file migration to Ipex in the background
                                this.migrateFileFromSeliseToIpex(fileId, fileResponse).subscribe();

                                return metadata;
                            })
                        )
                    );
                }
                return throwError(() => error);
            })
        );
    }

    // Helper methods
    private createFileUploadFormData(file: File, fileId: string, label?: string): FormData {
        const mimeType = this.mimeMapper.getMimeTypeByFileName(file?.name);

        const formData = new FormData();
        formData.append('file', file.slice(0, file.size, mimeType), file.name);
        formData.append('fileId', fileId);
        formData.append('labels', label || '');

        return formData;
    }

    private handle404Fallback<T>(
        operation: string,
        fileId: string,
        fallbackOperation: () => Observable<T>
    ): Observable<T> {
        return fallbackOperation().pipe(
            map(result => {
                return result;
            }),
            catchError(() => {
                return throwError(
                    () => new Error(`${operation} ${fileId} not found in both Ipex and Selise File Services`)
                );
            })
        );
    }

    private migrateFileFromSeliseToIpex(fileId: string, fileResponse?: any, label?: string): Observable<Blob> {
        const performMigration = (metadata: any) => {
            console.warn('migrating file from selise to ipex', fileId);
            const originalFileName = metadata.Name || `file_${fileId}`;

            return this.fileService.getFileAsBlob(metadata).pipe(
                switchMap(blob => {
                    const file = new File([blob], originalFileName, {
                        type: blob.type || metadata.Type || 'application/octet-stream'
                    });

                    // Upload the file to Ipex File Service in the background
                    console.warn('uploading file to ipex', fileId, `with ${label} label`);
                    this.uploadWithId(file, fileId, label)
                        .pipe(
                            tap(() => console.info(`Background upload completed for file ${fileId}`)),
                            catchError(error => {
                                console.warn(`Background upload failed for file ${fileId}:`, error);
                                return of(null);
                            })
                        )
                        .subscribe();

                    return of(blob);
                })
            );
        };

        if (fileResponse) {
            return performMigration(fileResponse);
        } else {
            return this.fileService.getFileById(fileId).pipe(
                switchMap((fileResponse: any) => {
                    return performMigration(fileResponse);
                }),
                catchError(() => {
                    return throwError(() => new Error(`File ${fileId} not found in Selise File Service`));
                })
            );
        }
    }
    public downloadAndSaveFileById(
        fileId: string,
        fileName?: string,
        label?: string,
        imageSize?: string
    ): Observable<Blob> {
        if (!fileId) {
            return throwError(() => new Error('Invalid file ID provided'));
        }

        let downloadEndpoint = `${this.fileServiceBaseUrl}fileservice/v1/file/getFile?id=${fileId}`;

        if (imageSize) {
            downloadEndpoint += `&imageSize=${imageSize || SCConfig.ImageSize.ORIGINAL}`;
        }

        return this.httpClient
            .get(downloadEndpoint, {
                responseType: 'blob'
            })
            .pipe(
                tap(blob => {
                    if (blob && fileName) {
                        this.downloadFileFromBlob(blob, fileName);
                    }
                }),
                catchError(error => {
                    if (error.status === 404) {
                        return this.handle404Fallback('File download', fileId, () =>
                            this.migrateFileFromSeliseToIpex(fileId, undefined, label).pipe(
                                tap(blob => {
                                    if (blob && fileName) {
                                        this.downloadFileFromBlob(blob, fileName);
                                    }
                                })
                            )
                        );
                    }

                    return throwError(() => error);
                })
            );
    }

    public getPreviewUrl(fileId: string, fileName: string): Observable<string> {
        return this.downloadFileById(fileId).pipe(
            map(blob => {
                const mimeType = this.mimeMapper.getMimeTypeByFileName(fileName);
                const fixedBlob = new Blob([blob], { type: mimeType });

                return URL.createObjectURL(fixedBlob);
            }),
            catchError(error => {
                console.error(`Error downloading file for preview: ${fileId}`, error);
                return throwError(() => error);
            })
        );
    }
}
